"use client"

import { BlockchainIcon } from "@/app/blockchains/blockchain-icon/blockchain-icon"
import {
  useBlockchain,
  useNativeCurrency,
} from "@/app/blockchains/use-blockchain"
import { DropMoleculeReatom } from "@/app/drop/store/drop-store.reatom"
import { useDropDisplayPrice } from "@/app/drop/store/hooks"
import { CloseIcon } from "@/app/icons/close"
import { PriceInCrypto, PriceInUsd } from "@/app/price"
import { BlockchainBalanceSdkProviderMoleculeReatom } from "@/app/sdk/blockchains/blockchain-balance-sdk-provider.reatom"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import type { Blockchain } from "@anaconda/core/blockchain/types"
import type { MinimalDrop } from "@anaconda/core/drop/types"
import type { PriceValue } from "@anaconda/core/price/types"
import { Trans, useLingui } from "@lingui/react/macro"
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  RadioGroup,
  RadioGroupItem,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  useIsMobile,
} from "@rarible_int/anaconda_ui_components"
import { computed, withAsyncData, wrap } from "@reatom/core"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import Image from "next/image"
import { useMemo, useState } from "react"
import type { Address } from "viem"

export interface MultiChainMintCheckoutProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  drop: MinimalDrop
  onMint: (blockchain: Blockchain) => void
}

const NetworkOption = reatomComponent<{
  blockchain: Blockchain
  price: PriceValue
  balance: string
  isCrosschainMint: boolean
}>(({ blockchain, price, balance, isCrosschainMint }) => {
  const { name } = useBlockchain(blockchain)
  const nativeCurrency = useNativeCurrency(blockchain)

  return (
    <label
      htmlFor={blockchain}
      className="flex h-14 cursor-pointer items-center gap-3 rounded-lg pr-2 hover:bg-foreground/5"
    >
      <div className="flex flex-1 items-center gap-3 py-2 pl-1.5">
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center">
          <BlockchainIcon blockchain={blockchain} className="h-8 w-8" />
        </div>
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <div className="font-body font-medium text-foreground text-sm leading-5 tracking-[-0.14px]">
              {name}
            </div>
            {isCrosschainMint && (
              <span className="rounded-full border border-brand px-2 py-0.5 font-body font-medium text-brand text-[10px] leading-[0.7rem] tracking-[-0.24px]">
                <Trans>Crosschain Mint</Trans>
              </span>
            )}
          </div>
          <div className="font-body font-medium text-foreground/60 text-xs leading-4 tracking-[-0.24px]">
            <Trans>
              Balance:{" "}
              <PriceInCrypto amount={balance} currency={nativeCurrency} />
            </Trans>
          </div>
        </div>
      </div>
      <RadioGroupItem value={blockchain} id={blockchain} />
    </label>
  )
}, "NetworkOption")

export const MultiChainMintCheckout =
  reatomComponent<MultiChainMintCheckoutProps>(
    ({ open, onOpenChange, drop, onMint }) => {
      const { t } = useLingui()
      const isMobile = useIsMobile()
      const { currentPhaseAtom } = useMolecule(DropMoleculeReatom)
      const currentPhase = currentPhaseAtom()
      const displayPrice = useDropDisplayPrice(drop)
      const { balanceSDKAtom } = useMolecule(
        BlockchainBalanceSdkProviderMoleculeReatom,
      )
      const { address: addressAtom } = useMolecule(WalletConnectMoleculeReatom)

      const availableBlockchains = useMemo<Blockchain[]>(() => {
        if (
          currentPhase?.pricesPerChain &&
          currentPhase.pricesPerChain.length > 0
        ) {
          return currentPhase.pricesPerChain.map((item) => item.blockchain)
        }
        return [drop.blockchain]
      }, [currentPhase, drop.blockchain])

      // Create balance atoms for each available blockchain
      const balanceAtoms = useMemo(() => {
        const atoms = new Map<Blockchain, ReturnType<typeof computed<string>>>()
        for (const blockchain of availableBlockchains) {
          const balanceAtomAsync = computed(async () => {
            const balanceSDK = balanceSDKAtom()
            const address = addressAtom()

            if (!balanceSDK || !address) {
              return "0"
            }

            try {
              const balance = await wrap(
                balanceSDK.getNativeBalance({
                  blockchain,
                  address: address as Address,
                }),
              )
              return balance
            } catch (error) {
              console.error(
                `Error fetching native balance for ${blockchain}:`,
                error,
              )
              return "0"
            }
          }, `balanceDataAsync-${blockchain}`).extend(
            withAsyncData({ initState: "0" }),
          )

          const balanceAtom = computed(() => {
            balanceAtomAsync.ready()
            return balanceAtomAsync.data() ?? "0"
          }, `balanceDataWrapper-${blockchain}`)

          atoms.set(blockchain, balanceAtom)
        }
        return atoms
      }, [availableBlockchains, balanceSDKAtom, addressAtom])

      // Read all balance atoms directly in render to ensure reactivity
      // This ensures the component rerenders when any balance updates
      // DO NOT use useMemo here - we need to read atoms on every render
      const balances = new Map<Blockchain, string>()
      for (const blockchain of availableBlockchains) {
        const balanceAtom = balanceAtoms.get(blockchain)
        if (balanceAtom) {
          // Read the atom directly to subscribe to changes
          // This will trigger rerenders when the atom value changes
          balances.set(blockchain, balanceAtom() as string)
        } else {
          balances.set(blockchain, "0")
        }
      }

      const [selectedBlockchain, setSelectedBlockchain] = useState<Blockchain>(
        availableBlockchains[0] ?? drop.blockchain,
      )

      const selectedPrice = useMemo(() => {
        const pricePerChain = currentPhase?.pricesPerChain?.find(
          (item) => item.blockchain === selectedBlockchain,
        )
        if (pricePerChain) {
          return pricePerChain.price
        }
        return currentPhase?.price || displayPrice
      }, [currentPhase, selectedBlockchain, displayPrice])

      const isNotUnlimitedSupply =
        currentPhase?.maxClaimableSupply || drop.totalSupply

      // Shared header component
      const Header = () => {
        if (isMobile) {
          return (
            <SheetHeader className="pb-4">
              <SheetTitle className="text-center font-headline font-medium text-[18px] text-foreground leading-6">
                <Trans>Mint checkout</Trans>
              </SheetTitle>
            </SheetHeader>
          )
        }

        return (
          <div className="flex w-full items-center justify-between pb-4">
            <div className="flex h-6 w-6 items-center justify-center p-1.5">
              <div className="h-3 w-3" />
            </div>
            <DialogHeader className="flex-1">
              <DialogTitle className="text-center font-headline font-medium text-[18px] text-foreground leading-6">
                <Trans>Mint checkout</Trans>
              </DialogTitle>
            </DialogHeader>
            <DialogClose asChild>
              <button
                type="button"
                className="flex h-6 w-6 items-center justify-center p-1.5"
                aria-label={t`Close`}
              >
                <CloseIcon className="h-3 w-3" />
              </button>
            </DialogClose>
          </div>
        )
      }

      // Shared item details component
      const ItemDetails = () => (
        <div className="w-full rounded-md border border-foreground/10 p-2">
          <div className="flex h-14 items-center gap-3 rounded-lg pr-2">
            <div className="flex flex-1 items-center gap-3 py-2 pl-1.5">
              <div className="relative h-8 w-8 flex-shrink-0">
                <Image
                  src={drop.media.url}
                  alt={drop.title}
                  width={32}
                  height={32}
                  className="h-8 w-8 flex-shrink-0 rounded"
                />
                <div className="absolute right-0 bottom-0 flex h-4 w-4 translate-x-1 translate-y-1 items-center justify-center rounded-full">
                  <BlockchainIcon
                    blockchain={drop.blockchain}
                    className="h-4 w-4"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-0.5">
                <div className="flex items-start gap-0.5">
                  <div className="font-body font-medium text-foreground text-sm leading-5 tracking-[-0.14px]">
                    {drop.title}
                  </div>
                </div>
                {isNotUnlimitedSupply && (
                  <div className="font-body font-medium text-foreground/60 text-xs leading-4 tracking-[-0.24px]">
                    <Trans>
                      {currentPhase?.maxClaimableSupply ||
                        drop.totalSupply ||
                        1}{" "}
                      copies
                    </Trans>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end">
              <PriceInCrypto
                amount={displayPrice.amount}
                currency={displayPrice.currency}
                className="font-body font-medium text-foreground text-sm leading-5 tracking-[-0.14px]"
              />
              <PriceInUsd
                amount={displayPrice.amount}
                currency={displayPrice.currency}
                className="font-body font-medium text-foreground text-xs leading-4 tracking-[-0.24px] opacity-60"
              />
            </div>
          </div>
        </div>
      )

      // Shared network selection component
      const NetworkSelection = () => (
        <div
          className={`flex w-full flex-col ${isMobile ? "gap-2" : "items-start gap-3"} pt-2`}
        >
          <div className="px-4">
            <h3 className="font-body font-medium text-foreground text-sm leading-5 tracking-[-0.14px]">
              <Trans>Choose network to pay</Trans>
            </h3>
          </div>
          <div className="w-full rounded-md border border-foreground/10 p-2">
            <RadioGroup
              value={selectedBlockchain}
              onValueChange={(value) =>
                setSelectedBlockchain(value as Blockchain)
              }
            >
              <div className="flex flex-col">
                {availableBlockchains.map((blockchain) => {
                  const pricePerChain = currentPhase?.pricesPerChain?.find(
                    (item) => item.blockchain === blockchain,
                  )
                  const isCrosschainMint = blockchain !== drop.blockchain
                  return (
                    <NetworkOption
                      key={blockchain}
                      blockchain={blockchain}
                      price={
                        pricePerChain?.price ||
                        currentPhase?.price ||
                        displayPrice
                      }
                      balance={balances.get(blockchain) ?? "0"}
                      isCrosschainMint={isCrosschainMint}
                    />
                  )
                })}
              </div>
            </RadioGroup>
          </div>
        </div>
      )

      // Shared mint button component
      const MintButton = () => (
        <Button
          onClick={() => onMint(selectedBlockchain)}
          className="h-9 w-full rounded-md bg-brand px-4 py-2 font-mono text-[#0A0A0A] text-sm leading-5 hover:bg-brand/90"
        >
          <Trans>
            Mint for{" "}
            <PriceInCrypto
              amount={selectedPrice.amount}
              currency={selectedPrice.currency}
              className="[&_.currency-symbol]:text-[#0A0A0A]"
            />
          </Trans>
        </Button>
      )

      // Shared footer component
      const Footer = () => (
        <div className="flex items-center justify-center gap-1.5 px-0 pt-4 pb-1 opacity-60">
          <span className="font-headline font-normal text-[15px] text-foreground leading-normal">
            <Trans>Powered by</Trans>
          </span>
          <div className="relative h-[26px] w-[90px]">
            <Image
              src="/images/espresso-logo.svg"
              alt="Espresso"
              width={90}
              height={26}
              className="object-contain"
            />
          </div>
        </div>
      )

      // Shared content for both mobile and desktop
      const Content = () => (
        <div
          className={`flex flex-col ${isMobile ? "gap-4" : "items-center gap-4"}`}
        >
          <Header />
          <ItemDetails />
          <NetworkSelection />
          {isMobile ? (
            <div className="flex flex-col gap-3">
              <MintButton />
            </div>
          ) : (
            <MintButton />
          )}
          <Footer />
        </div>
      )

      if (isMobile) {
        return (
          <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
              className="w-full rounded-t-xl border-foreground/10 bg-[#0A0A0A] p-6"
              side="bottom"
              aria-describedby="mint-checkout-sheet"
            >
              <Content />
            </SheetContent>
          </Sheet>
        )
      }

      return (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent
            className="max-w-[394px] border-foreground/10 bg-[#0A0A0A] p-4"
            aria-describedby="mint-checkout-dialog"
          >
            <Content />
          </DialogContent>
        </Dialog>
      )
    },
    "MultiChainMintCheckout",
  )
